import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { appNav } from "../routes/appNavigation";
import "./GlobalSearch.css";

type SearchHit = {
  id: string;
  label: string;
  group: string;
  path: string;
};

const STATIC_HITS: SearchHit[] = [
  { id: "calendar", label: "Calendar", group: "Navigate", path: appNav.calendar() },
  { id: "events", label: "Events", group: "Navigate", path: appNav.events() },
  { id: "finance", label: "Finance", group: "Navigate", path: appNav.finance() },
  { id: "guests", label: "Guest List", group: "Directory", path: appNav.guests() },
  { id: "profile", label: "Profile", group: "Navigate", path: appNav.profile() },
  { id: "artists", label: "Artists", group: "Directory", path: appNav.artists() },
  { id: "venues", label: "Venues", group: "Directory", path: appNav.venues() },
  { id: "contacts", label: "Contacts", group: "Directory", path: appNav.contacts() },
  { id: "new-event", label: "Add event", group: "Actions", path: appNav.newEvent() },
];

type Props = {
  className?: string;
  compact?: boolean;
};

export const GlobalSearch: React.FC<Props> = ({ className = "", compact = false }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const hits = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return STATIC_HITS.slice(0, 6);
    const navHits = STATIC_HITS.filter(
      (h) => h.label.toLowerCase().includes(q) || h.group.toLowerCase().includes(q),
    );
    const eventSearch: SearchHit = {
      id: "events-q",
      label: `Search events for “${query.trim()}”`,
      group: "Events",
      path: `${appNav.events()}?q=${encodeURIComponent(query.trim())}`,
    };
    return [eventSearch, ...navHits].slice(0, 8);
  }, [query]);

  useEffect(() => {
    setActive(0);
  }, [query, open]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const go = (hit: SearchHit) => {
    setOpen(false);
    setQuery("");
    navigate(hit.path);
  };

  return (
    <div
      ref={rootRef}
      className={`global-search${compact ? " global-search--compact" : ""}${className ? ` ${className}` : ""}`}
    >
      <label className="global-search__field">
        <span className="visually-hidden">Search</span>
        <span className="global-search__icon" aria-hidden>
          ⌕
        </span>
        <input
          className="global-search__input"
          type="search"
          placeholder="Search…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (!open && (e.key === "ArrowDown" || e.key === "Enter")) setOpen(true);
            if (e.key === "Escape") {
              setOpen(false);
              (e.target as HTMLInputElement).blur();
            }
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setActive((i) => Math.min(hits.length - 1, i + 1));
            }
            if (e.key === "ArrowUp") {
              e.preventDefault();
              setActive((i) => Math.max(0, i - 1));
            }
            if (e.key === "Enter" && hits[active]) {
              e.preventDefault();
              go(hits[active]);
            }
          }}
        />
      </label>
      {open ? (
        <ul className="global-search__menu" role="listbox">
          {hits.length === 0 ? (
            <li className="global-search__empty">No matches</li>
          ) : (
            hits.map((hit, index) => (
              <li key={hit.id}>
                <button
                  type="button"
                  className={`global-search__item${index === active ? " is-active" : ""}`}
                  onMouseEnter={() => setActive(index)}
                  onClick={() => go(hit)}
                >
                  <span className="global-search__item-label">{hit.label}</span>
                  <span className="global-search__item-group">{hit.group}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
};
